import Icon, { COLORS, ICONS } from '../../general-ui/icon';
import { useState, useMemo, useEffect, useCallback } from 'react';
// import Modal from '../../general-ui/modal/modal';
// import TextInput from '../../general-ui/text-input/text';
// import { validateUrl } from '../../../utils/utils';
import { getEventLink } from './event-card';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { EventsState } from '../../../store/types';
import { clearPublishedUrl, clearSelectedEvent, clearWorkingEvent, getPublishedEventUrl } from '../../../store/actions/admin/create-event';
import './_event-details-header.scss';
import EventSettings from './event-settings-modal';
import { getStartTime } from '../../../utils/get-event-start-time';
import { Status } from '../../../types/event';
import { eventPath, getEventNameURL } from '../../live-event/utils';
import { BrandliveEvent } from '../../../types/working-model';
import { getDefaultLanguageCode } from '../../../utils/get-language';
import { getPrefixUrl } from '../../../utils/utils';
import classNames from 'classnames';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { Tooltip } from '../../general-ui/tooltip/tooltip';
import { AuthWrapper } from '../authentication/AuthWrapper';

export function EventDetailsHeader(): JSX.Element {

	const {
		CreateEventReducer: { selectedEvent, publishedUrl },
	} = useSelector((state: EventsState) => state);

	const dispatch = useDispatch();

	const [settingsModalOpen, setSettingsModalOpen] = useState(false);
	const [isLive] = useState(false);

	function getStatus() {
		if (isLive)
			return (
				<div className="event-details-status live">
					{"Live"}
				</div>
			);
		switch (selectedEvent?.status) {
			case 0: {
				return (
					<div className="event-details-status">
						{Status[selectedEvent.status]}
					</div>
				);
			}
			case 1: {
				return (
					<div className="event-details-status published">
						{Status[selectedEvent.status]}
					</div>
				);
			}
			case 2: {
				return (
					<div className="event-details-status unpublished">
						{Status[selectedEvent.status]}
					</div>
				);
			}
			default: {
				return (
					<div className="event-details-status error">
						{"None"}
					</div>
				);
			}
		}
	}

	const handleLinkClick = useCallback(() => {
		dispatch(clearWorkingEvent());
	}, [dispatch]);

	const startTime = useMemo(() => {
		return getStartTime(selectedEvent!, true);
	}, [selectedEvent]);

	const editEventSettingsButton = (
		<button disabled={!publishedUrl} className="small" onClick={() => setSettingsModalOpen(true)}><Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} /><span>Edit</span></button>
	);
	return (
		<div className="event-details-header-container">
			<div className="left">
				<button
					onClick={() => dispatch(clearSelectedEvent())}
					className="return-button no-style"
				>
					<Icon name={ICONS.ARROW_LEFT} size={16} color={COLORS.WHITE} />
				</button>
				<div className="event-details-title">
					<h6>{selectedEvent?.name}</h6>
					<div className="event-details-info">
						{getStatus()}
						<div className="event-details-timestamp">
							{startTime ? ` • Starts on ${startTime}` : ""}
						</div>
					</div>
				</div>
			</div>

			<div className="right">
				<div className="events-details-buttons reverse-tooltip">
					<AuthWrapper>
						{publishedUrl ?
							editEventSettingsButton
							:
							<Tooltip tooltip="Publish event to edit the event’s URL" >{editEventSettingsButton}</Tooltip>
						}
					</AuthWrapper>
					<CopyEventUrlButton event={selectedEvent} />
					<AuthWrapper>
						<Link onClick={handleLinkClick} to={getEventLink(selectedEvent!)}><button className="small primary">Edit event</button></Link>
					</AuthWrapper>
				</div>
			</div>
			<EventSettings
				event={selectedEvent!}
				open={settingsModalOpen}
				setOpen={setSettingsModalOpen}
			/>
		</div>
	);
}

interface ICopyEventUrlButtonProps {
	event: BrandliveEvent | null;
}

export const CopyEventUrlButton: React.FC<ICopyEventUrlButtonProps> = ({ event }) => {
	const {
		channels,
		user,
		token,
		publishedUrl
	} = useTypedSelector(state => ({
		channels: state.AuthReducer.channels,
		user: state.AuthReducer.user,
		token: state.AuthReducer.token,
		publishedUrl: state.CreateEventReducer.publishedUrl
	}));

	const dispatch = useDispatch();

	const [toastCopied, setToastCopied] = useState(false);
	const [hasBeenPublished, setHasBeenPublished] = useState<boolean | null>(null);

	useEffect(() => {
		if (toastCopied) setTimeout(() => setToastCopied(false), 2000);
	}, [toastCopied]);

	useEffect(() => {
		setHasBeenPublished(false);
		if (publishedUrl === null && event?.uuid) {
			dispatch(getPublishedEventUrl(token!, event.uuid));
		}

		if (publishedUrl) {
			setHasBeenPublished(true);
		} else {
			setHasBeenPublished(false);
		}
	}, [dispatch, event?.uuid, publishedUrl, token]);

	useEffect(() => {
		return () => {
			dispatch(clearPublishedUrl());
		};
	}, [dispatch]);

	const channel = useMemo(() => {
		return channels.find((channel) => channel.channel === user!.active_channel);
	}, [channels, user]);

	function copyEventUrl() {
		const isDevEnv = process.env.NODE_ENV === 'development';
		let eventLink = '';

		if (isDevEnv) {
			eventLink = `localhost:3000/event/${event?.uuid}/${getDefaultLanguageCode(null)}`;
		} else {
			const eventUrl = publishedUrl ? publishedUrl : getEventNameURL(event as BrandliveEvent);
			const eventName = eventPath(eventUrl, event?.uuid);
			const languageCode = getDefaultLanguageCode(event?.sessions?.[0]);

			eventLink = `${getPrefixUrl(channel!.url)}/${eventName}/${languageCode}`;
		}

		navigator.clipboard.writeText(eventLink);
		setToastCopied(true);
	}

	const copyButton = <button
		className="small"
		disabled={!hasBeenPublished}
		onClick={copyEventUrl}
	>
		{toastCopied ? 'Event link copied!' : (
			<>
				<Icon name={ICONS.LINK} size={12} color={COLORS.WHITE} /><span>Copy event link</span>
			</>
		)}
	</button>;

	return (
		<div className={classNames("copy-event reverse-tooltip", { toast: toastCopied })}>
			{
				event && hasBeenPublished ?
					copyButton
					:
					<Tooltip tooltip="Publish event to copy event’s URL">{copyButton}</Tooltip>
			}
		</div>
	);
};